import type { EirFormComment } from "@/Domain/EirFormComment";
import type { EnumVisitType } from "@/Domain/EnumVisitType";
import type { InspectionFacilityContact } from "@/Domain/InspectionFacilityContact";
import type { InspectionRfrTeamMember } from "@/Domain/InspectionRfrTeamMember";
import type { VisitInspectionAnswerDisplay } from "@/Domain/VisitInspectionAnswerDisplay";
import type { InspectionIncorrectIngredient } from "@/Domain/InspectionIncorrectIngredient";
import type { InspectionIncorrectProduct } from "@/Domain/InspectionIncorrectProduct";
import type { TaskQuestionItem } from "@/Domain/TaskQuestionItem";
import type { UploadFileInfo } from "@progress/kendo-vue-upload";
import type { InspectionRfrPlant } from "@/Domain/InspectionRfrPlant";
import type { EirRcPerson } from "@/Domain/EirRcPerson";
import { defineStore } from "pinia";

export interface FormData {
  visitType: { text: string; value: EnumVisitType | null };
  visitDate: Date | undefined;
  timeSpent: string;
  requestingRc: EirRcPerson | null;
  selectedRc: EirRcPerson | null;
  plantName: string;
  companyName: string;
  rfrName: string;
  rcName: string;
  plant: InspectionRfrPlant | null;
  plantAddress: string;
  groupLeader: string;
  visitDateRange: string;
  plantCity: string;
  plantState: string;
  plantCountry: string;
  plantContact: string;
  sentByRcName: string;
  sentByRcEmail: string;
  plantContactEmail: string;
  plantContactPhone: string;
  plantGuide: InspectionFacilityContact | null;
  guideFirstName: string;
  guideLastName: string;
  guideEmail: string;
  guidePhone: string;
  teamMember: InspectionRfrTeamMember | null;
  generalComments: string;
  savedGeneralComments: EirFormComment[];
  confidentialComment: string;
  savedConfidentialComment: EirFormComment[];
  incorrectIngredientsFound: boolean;
  incorrectProductsFound: boolean;
  incorrectIngredients: InspectionIncorrectIngredient[];
  incorrectProducts: InspectionIncorrectProduct[];
  incorrectIngredientComments: string;
  incorrectIngredientSavedComments: EirFormComment[];
  incorrectProductComments: string;
  incorrectProductSavedComments: EirFormComment[];
  urgentMatters: string;
  savedUrgentMatters: EirFormComment[];
  isUrgent: boolean;
  isUrgentComment: string;
  savedIsUrgentComment: EirFormComment[];
  sendEmailCopy: boolean;
  emailRecipients: string;
  hideSpecialTasks: boolean;
  specialTaskQuestions: TaskQuestionItem[];
  uploadedDocs: UploadFileInfo[];
  reopenComment: string;
  reopenSavedComment: EirFormComment[];
  virtualVisit: boolean;
  jewishOwned: boolean | null;
  invalidFields: string[];
  answers: VisitInspectionAnswerDisplay[];
}

interface Section {
  number: number | undefined;
  name: string;
  expanded: boolean;
  visibilityRatio: number;
}

export const useInspectionStore = defineStore("inspection", {
  state: () => {
    return {
      isGroupVisit: false,
      visitType: "",
      visitStatus: "",
      isUrgentStatus: false,
      lastCommentDate: null as Date | null,
      formLoaded: false,
      formData: {} as FormData,
      hideVisitId: false,
      sections: {} as Record<string, Section>,
      activeSectionId: "",
      scrollToSectionId: "",
      previousResponsesExpanded: false,
      companyJewishOwned: null as boolean | null,
    };
  },
  getters: {
    fieldIsInvalid: (state) => (field: string) => {
      return state.formData.invalidFields.includes(field);
    },
  },
  actions: {
    addOrUpdateSection(sectionId: string, sectionNumber: number | undefined, sectionName: string, expanded: boolean) {
      const existingSection = this.sections[sectionId];
      if (existingSection) {
        existingSection.number = sectionNumber;
        existingSection.name = sectionName;
        existingSection.expanded = expanded;
      } else {
        this.sections[sectionId] = { number: sectionNumber, name: sectionName, expanded, visibilityRatio: 0 };
      }
    },
    removeSection(sectionId: string) {
      delete this.sections[sectionId];
    },
    expandSection(sectionId: string, setActive: boolean = false, updateVisibility: boolean = true) {
      const section = this.sections[sectionId];
      if (section) section.expanded = true;
      if (setActive) {
        this.activeSectionId = sectionId;
        this.scrollToSectionId = sectionId;
      }
      if (updateVisibility) this.updateSectionVisibility();
    },
    collapseSection(sectionId: string, updateVisibility: boolean = true) {
      const section = this.sections[sectionId];
      if (section) section.expanded = false;
      if (updateVisibility) this.updateSectionVisibility();
    },
    expandAllSections() {
      Object.keys(this.sections).forEach((sectionId) => {
        this.expandSection(sectionId, false, false);
      });
      this.updateSectionVisibility();
    },
    collapseAllSections() {
      Object.keys(this.sections).forEach((sectionId) => {
        this.collapseSection(sectionId, false);
      });
      this.updateSectionVisibility();
    },
    togglePreviousResponses() {
      this.previousResponsesExpanded = !this.previousResponsesExpanded;
    },
    collapsePreviousResponses() {
      this.previousResponsesExpanded = false;
    },
    expandPreviousResponses() {
      this.previousResponsesExpanded = true;
    },
    setActiveSection(sectionId: string) {
      this.activeSectionId = sectionId;
    },
    updateSectionVisibility(sectionId?: string, visibilityRatio?: number) {
      if (sectionId && visibilityRatio != undefined) {
        const section = this.sections[sectionId];
        if (section) {
          section.visibilityRatio = visibilityRatio;
        }
      }

      // Find the section with the highest visibility ratio
      let maxRatio = 0;
      let mostVisibleSectionId = "";

      Object.keys(this.sections)
        .sort((a, b) => (this.sections[a].number ?? 0) - (this.sections[b].number ?? 0))
        .forEach((id) => {
          const sectionItem = this.sections[id];
          // Limit to 0.95 to prevent rapid switching when multiple short sections are mostly visible
          // Ignore collapsed sections
          if (maxRatio < 0.95 && sectionItem.expanded && sectionItem.visibilityRatio > maxRatio) {
            maxRatio = sectionItem.visibilityRatio;
            mostVisibleSectionId = id;
          }
        });

      // Only update active section if we have a significantly visible section (> 10% visible)
      if (maxRatio > 0.1 && mostVisibleSectionId !== this.activeSectionId) {
        this.activeSectionId = mostVisibleSectionId;
      }
    },
  },
});
